import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, from, Subject, firstValueFrom } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import {
  getAutoprintQueue,
  getAutoprintSubscriptions,
  getAutoprintSubscriptionsByStoreId,
  getRecentlyPrintedOrders,
} from './+state/printers.selectors';
import {
  AUTOPRINT_PRINTED_ORDERS_KEY,
  AUTOPRINT_SUBSCRIBED_SINCE_KEY,
  AutoprintJobContext,
  AutoprintQueueElement,
  AutoprintSubscriptions,
  isSameOrOlderVersion,
  latestRecordFor,
  PRINTER_MODE_LABELS,
  PrintedOrderRecord,
  PrinterInfo,
  printedRecordFor,
  printerKey,
  recoveryTargets,
  toPrintedOrderRecords,
} from './printers';
import {
  MarkOrderPrinted,
  RecordAutoprintAttempt,
  UpdateAutoprintQueue,
  UpdateAutoprintSubscriptions,
} from './+state/printers.actions';
import { OrderPdfResponse } from '../stores/store-order/store-order';
import { HttpClient, HttpResponse } from '@angular/common/http';
import EscPosEncoder from 'esc-pos-encoder-ionic';
import { imageJob } from './esc-pos-job';
import { STARPRINTER_EMULATION } from './star';
import { ToastrService } from 'ngx-toastr';
import { GoToast } from 'src/app/shared/go-toast.component';
import { LogService } from '../shared/logging/LogService';
import { HelperService } from '../public/helper.service';
import { Printer } from '@awesome-cordova-plugins/printer/ngx';
import { MobileFileService } from '../shared/mobile.file.service';
import { GoPrinterService } from './goPrinter.service';
import { Capacitor } from '@capacitor/core';
import { LocalStorageService } from '../local-storage.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class PrinterService implements OnDestroy {
  subscriptions: AutoprintSubscriptions;
  autoPrintQueue$: Observable<AutoprintQueueElement[]>;
  printTaskCompleted = true;
  private autoprintQueueDestroy$ = new Subject<void>();
  private autoprintQueueInterval: ReturnType<typeof setInterval> | null = null;
  airPrinter: Printer;

  constructor(
    private store: Store,
    private http: HttpClient,
    private toastr: ToastrService,
    private logger: LogService,
    private helperService: HelperService,
    private goPrinterService: GoPrinterService,
    private mobileFileService: MobileFileService,
    private storageService: LocalStorageService,
    private translate: TranslateService,
  ) {
    this.airPrinter = new Printer();

    this.subscriptions = {};
    this.autoPrintQueue$ = this.store.select(getAutoprintQueue);
    this.autoprintQueueInterval = setInterval(() => {
      void this.drainAutoPrintQueue();
    }, 5000);
  }

  /**
   * Drain the autoprint queue until it is empty.
   *
   * Safe to call from the foreground `setInterval` and from a background push
   * handler — the OS only grants the JS context a short window after a wake-up,
   * and the next push wakes us again.
   *
   * It drains the whole queue rather than one job per call because in the
   * background the interval that would pick up the rest is paused, which is the
   * pause this story exists to work around: two orders arriving close together
   * queue two jobs, the second call hits the in-flight guard and returns, and
   * order 2 would then wait for the app to be foregrounded — and foreground
   * reconciliation correctly skips it, because it is still queued. Card #4514.
   */
  async drainAutoPrintQueue(): Promise<void> {
    if (!this.printTaskCompleted) {
      return;
    }
    // Claimed here, before the first `await`, so two callers cannot both pass
    // the guard and take the same head element. It used to be set after the
    // queue had been read, which was unreachable while the five-second interval
    // was the only caller and is not now that every queued job triggers a drain.
    //
    // This method is the only writer of the flag from here on. `autoPrint` used
    // to set it back to `true` the moment each receipt settled, while this loop
    // was still running: the five-second tick or the next queued job then
    // started a second drain, which took the job this one was printing — the
    // queue head, still in the queue — and printed it again. Three orders at
    // once came out as four receipts. Card #4514.
    this.printTaskCompleted = false;
    // Belt and braces against spinning on a head element that will not leave
    // the queue: every iteration must take a job it has not taken before. Kept
    // by job, not by order: a device that prints each order on two printers
    // queues two jobs for one order, and so does a backend re-announce of an
    // order whose first job failed — keying on the order stopped the drain at
    // the second one.
    const attempted = new Set<AutoprintQueueElement>();
    try {
      for (;;) {
        const autoprintQueue = await firstValueFrom(
          this.store.select(getAutoprintQueue).pipe(take(1)),
        );
        if (!autoprintQueue || autoprintQueue.length === 0) {
          return;
        }
        const autoprintElem = autoprintQueue[0];
        if (attempted.has(autoprintElem)) {
          return;
        }
        attempted.add(autoprintElem);
        await this.printOneQueuedJob(autoprintElem);
      }
    } finally {
      this.printTaskCompleted = true;
      // A job queued between the last read of the queue and the release above
      // found the drain still claimed and returned; nothing else would pick it
      // up until the five-second tick, which a backgrounded app may not get.
      this.drainAgainIfJobsWaiting(attempted);
    }
  }

  /** Starts another drain when a job this one never took is still queued. */
  private drainAgainIfJobsWaiting(attempted: Set<AutoprintQueueElement>): void {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      if ((autoprintQueue ?? []).some((job) => !attempted.has(job))) {
        void this.drainAutoPrintQueue();
      }
    });
  }

  /** One job: print it, record it if the receipt came out, dequeue it either way. */
  private async printOneQueuedJob(
    autoprintElem: AutoprintQueueElement,
  ): Promise<void> {
    autoprintElem.status = 'INPROGRESS';
    this.updateQueue((autoprintQueue) => autoprintQueue);
    let printed = false;
    let threw = false;
    try {
      printed = await firstValueFrom(
        this.autoPrint(
          autoprintElem.printer,
          autoprintElem.storeId,
          autoprintElem.orderId,
        ),
      );
      // Only a receipt that actually reached the printer is recorded, because
      // this record is what stops foreground reconciliation re-queueing the
      // order. Marking it unconditionally — or from `finally` below — would
      // hide exactly the failed print reconciliation exists to recover, and
      // `autoPrint` reports a transport failure as `false` rather than by
      // throwing, so reaching this line is not on its own evidence that
      // anything was printed. Card #4514.
      //
      // Recorded per printer: with two printers, one succeeding says nothing
      // about the other, and recording the order as a whole hid the other
      // printer's failure from reconciliation for good.
      if (printed) {
        this.store.dispatch(
          new MarkOrderPrinted(
            autoprintElem.orderId,
            new Date().toISOString(),
            printerKey(autoprintElem.printer),
            autoprintElem.autoprint?.version,
          ),
        );
      }
    } catch (err) {
      threw = true;
      // A print failure must not break the queue: the job is dequeued below and
      // foreground reconciliation retries an automatic one. Logged, because
      // this used to be the one place an order could vanish without a trace.
      this.logger.error(
        `AutoPrint job for order ${autoprintElem.orderId} failed: `,
        err,
      );
    } finally {
      if (!printed && autoprintElem.autoprint) {
        // A throw is the receipt download failing (the invoice request), not
        // the printer: it says nothing about the other jobs for that printer.
        this.recordFailedAutoprint(autoprintElem, !threw);
      }
      this.removePrintFromQueue(autoprintElem);
    }
  }

  /**
   * Records an automatic job that did not print, so reconciliation retries it.
   *
   * A reconciliation job failing means its printer is still unreachable, so
   * the other reconciliation jobs waiting for that printer are taken out of the
   * queue too and left for the next pass. Each would otherwise spend up to the
   * plugin's 30-second connect timeout failing in turn, and every order that
   * arrived meanwhile — including the receipts the working printer could have
   * printed — would wait behind them. Card #4514.
   */
  private recordFailedAutoprint(
    autoprintElem: AutoprintQueueElement,
    printerFailed: boolean,
  ): void {
    const failedPrinter = printerKey(autoprintElem.printer);
    this.store.dispatch(
      new RecordAutoprintAttempt(
        autoprintElem.orderId,
        'failed',
        failedPrinter,
        autoprintElem.autoprint?.version,
      ),
    );
    if (autoprintElem.autoprint?.origin !== 'recovery' || !printerFailed) {
      return;
    }
    const heldBack: AutoprintQueueElement[] = [];
    this.updateQueue((autoprintQueue) => {
      for (let i = autoprintQueue.length - 1; i >= 0; i--) {
        const job = autoprintQueue[i];
        if (
          job !== autoprintElem &&
          job.status === 'NOTSTARTED' &&
          job.autoprint?.origin === 'recovery' &&
          printerKey(job.printer) === failedPrinter
        ) {
          heldBack.push(...autoprintQueue.splice(i, 1));
        }
      }
      return autoprintQueue;
    });
    // Recorded as failed with it, so the next pass sees them as finished and
    // retries them at once, rather than as still on their way to the printer.
    for (const job of heldBack) {
      this.store.dispatch(
        new RecordAutoprintAttempt(
          job.orderId,
          'failed',
          failedPrinter,
          job.autoprint?.version,
        ),
      );
    }
  }

  /**
   * Removes exactly this job, from the queue as it is now: the same order can
   * also be queued for another printer, and the queue may have been replaced
   * (autoprint switched off for a store) while this job was printing.
   */
  private removePrintFromQueue(autoprintElem: AutoprintQueueElement) {
    this.updateQueue((autoprintQueue) => {
      const printIndex = autoprintQueue.indexOf(autoprintElem);
      if (printIndex >= 0) {
        autoprintQueue.splice(printIndex, 1);
      }
      return autoprintQueue;
    });
  }

  /**
   * Applies `change` to the queue as it is in the store right now and
   * dispatches the result.
   *
   * The queue array is changed in place where `change` does, as it always has
   * been: every reader then sees a job the moment it is added, even before the
   * reducer has run, which is what keeps two jobs queued in the same tick from
   * missing each other.
   */
  private updateQueue(
    change: (
      autoprintQueue: AutoprintQueueElement[],
    ) => AutoprintQueueElement[],
  ): void {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      this.store.dispatch(
        new UpdateAutoprintQueue(change(autoprintQueue ?? [])),
      );
    });
  }

  isSupported() {
    // need some investigation...
    return true;
  }

  init(subscriptions: AutoprintSubscriptions): void {
    this.subscriptions = { ...subscriptions };
  }

  addSubscription(
    storeId: number,
    subscriptions: AutoprintSubscriptions,
  ): AutoprintSubscriptions {
    this.init(subscriptions);
    if (!Object.prototype.hasOwnProperty.call(this.subscriptions, storeId)) {
      this.subscriptions[`${storeId}`] = true;
      this.setSubscribedSince(storeId, new Date().toISOString());
      this.store.dispatch(new UpdateAutoprintSubscriptions(this.subscriptions));
    } else {
    }
    return this.subscriptions;
  }

  removeSubscription(
    storeId: number,
    subscriptions: AutoprintSubscriptions,
  ): AutoprintSubscriptions {
    this.subscriptions = { ...subscriptions };
    if (Object.prototype.hasOwnProperty.call(this.subscriptions, storeId)) {
      delete this.subscriptions[`${storeId}`];
      this.setSubscribedSince(storeId, null);

      this.clearAutoPrintQueue(storeId);
      this.store.dispatch(new UpdateAutoprintSubscriptions(this.subscriptions));
    } else {
    }
    return this.subscriptions;
  }

  /**
   * When autoprint was switched on for `storeId` on this device; `null` clears
   * it. See `AUTOPRINT_SUBSCRIBED_SINCE_KEY`. Card #4514.
   */
  private setSubscribedSince(storeId: number, since: string | null): void {
    const stored = this.storageService.getSavedState(
      AUTOPRINT_SUBSCRIBED_SINCE_KEY,
    );
    const subscribedSince: Record<string, string> =
      stored && typeof stored === 'object' && !Array.isArray(stored)
        ? { ...(stored as Record<string, string>) }
        : {};
    if (since) {
      subscribedSince[`${storeId}`] = since;
    } else {
      delete subscribedSince[`${storeId}`];
    }
    this.storageService.setSavedState(
      subscribedSince,
      AUTOPRINT_SUBSCRIBED_SINCE_KEY,
    );
  }

  requestPermission(): Observable<boolean> {
    if (this.isAndroid()) {
      return of(true);
    } else if (this.isIOS()) {
      return from(this.airPrinter.isAvailable());
    } else {
      return of(false);
    }
  }

  printContentUrlToAirPrintPrinter(contentUrl: string, printerUrl: string) {
    return from(this.airPrinter.print(contentUrl, { printer: printerUrl }));
  }

  isAndroid() {
    return this.helperService.isAndroid();
  }

  isIOS() {
    return Capacitor.getPlatform() === 'ios';
  }

  printContentUrl(contentUrl: string, printer: PrinterInfo) {
    const printerUrl = printer.url;
    const type = printer.type;

    if (type == 'AIRPRINT') {
      return this.printContentUrlToAirPrintPrinter(contentUrl, printerUrl);
    }

    return of(null);
  }

  saveFile(blob: Blob, fileName: string) {
    return from(this.mobileFileService.writeFileOnDevice(fileName, blob));
  }

  /**
   * Queues one receipt for one printer and starts a drain.
   *
   * `autoprint` is given only by the automatic paths — the order push and
   * foreground reconciliation (`PrintersEffects.onPrintJob`). Such a job is
   * dropped when the same receipt is already waiting in the queue or has
   * already come out, so an order that reaches this point twice prints once: a
   * push replayed when the app is opened after being killed, while the
   * cold-start reconciliation pass has re-queued the same order; or a push that
   * was held up and lands after reconciliation printed its order. A job a staff
   * member asked for by hand (`onSinglePrintJob`) passes no context and is
   * always queued. Card #4514.
   */
  addToAutoPrintQueue(
    printer: PrinterInfo,
    storeId: number,
    orderId: string,
    autoprint?: AutoprintJobContext,
  ): Observable<boolean> {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      if (!autoprintQueue) {
        autoprintQueue = [];
      }
      const job: AutoprintQueueElement = {
        storeId: storeId,
        orderId: orderId,
        printer: printer,
        status: 'NOTSTARTED',
        ...(autoprint ? { autoprint } : {}),
      };
      if (autoprint && this.isDuplicateAutoprintJob(autoprintQueue, job)) {
        return;
      }
      // A receipt the push or a staff member asked for goes ahead of any
      // reconciliation job still waiting: recovered orders are already late,
      // and a printer that turns out to be unreachable must not hold up the
      // orders arriving now. Card #4514.
      const firstWaitingRecovery =
        autoprint?.origin === 'recovery'
          ? -1
          : autoprintQueue.findIndex(
              (queued) =>
                queued.status === 'NOTSTARTED' &&
                queued.autoprint?.origin === 'recovery',
            );
      if (firstWaitingRecovery >= 0) {
        autoprintQueue.splice(firstWaitingRecovery, 0, job);
      } else {
        autoprintQueue.push(job);
      }
      this.store.dispatch(new UpdateAutoprintQueue(autoprintQueue));
      if (autoprint) {
        this.store.dispatch(
          new RecordAutoprintAttempt(
            orderId,
            'queued',
            printerKey(printer),
            autoprint.version,
          ),
        );
      }
      // Drain from here, the first point at which the job actually exists in
      // the queue, rather than from the push handler.
      //
      // `app.component.ts`'s `handleOrderPush` used to call
      // `drainAutoPrintQueue()` on the line after dispatching
      // `OrderDirectPrint`. That action only STARTS the effect chain
      // (onOrderDirectPrint -> download the receipt -> onDownloadOrderPdfSuccess
      // -> onPrintToEnabledPrinters -> onHandlePrintJobs -> onPrintJob) that
      // eventually arrives here, and none of it has run by the next
      // synchronous line — so that call always found an empty queue and never
      // printed anything, leaving the 5-second interval (which Android pauses
      // in the background) as the only real drain path. Here it fires for
      // every caller: the push path via `onPrintJob` and `onSinglePrintJob`.
      // Card #4514.
      void this.drainAutoPrintQueue();
    });
    return of(true);
  }

  /**
   * Whether an automatic job would print a receipt that is already queued or
   * already printed.
   *
   * Queued: the same order for the same printer is waiting, whichever path put
   * it there. Printed: a reconciliation job never reprints a receipt that came
   * out; a push job only skips one that came out for this version of the order
   * or a later one. A push carrying a newer version than the printed receipt is
   * the backend announcing the order again, and prints exactly as it did before
   * reconciliation existed.
   */
  private isDuplicateAutoprintJob(
    autoprintQueue: AutoprintQueueElement[],
    job: AutoprintQueueElement,
  ): boolean {
    const waiting = autoprintQueue.filter(
      (queued) => jobKey(queued) === jobKey(job),
    );
    if (waiting.length) {
      const covered = this.coveredByQueuedJobs(waiting, job);
      if (covered && waiting.every((queued) => !queued.autoprint)) {
        this.noteOwedBehindHandPrint(job);
      }
      return covered;
    }
    return this.alreadyHandled(job);
  }

  /**
   * Notes that the printer is owed an automatic job a print staff asked for by
   * hand is covering. A hand print records nothing if it fails, so without the
   * note the printer would be owed nothing; a hand print that succeeds records
   * the receipt over it. Card #4514.
   *
   * Only for a job that would have printed with no hand print in the way — not
   * a copy of a receipt that came out, nor a recovery job for a printer the pass
   * did not find owed — and only when the printer is not owed the order
   * already: a 'queued' or 'failed' record says so, and carries the version a
   * replayed push is checked against, which this note would replace.
   */
  private noteOwedBehindHandPrint(job: AutoprintQueueElement): void {
    const key = printerKey(job.printer);
    if (
      this.alreadyHandled(job) ||
      latestRecordFor(this.printedOrderRecords(), job.orderId, key)?.state
    ) {
      return;
    }
    this.store.dispatch(
      new RecordAutoprintAttempt(
        job.orderId,
        'queued',
        key,
        job.autoprint.version,
      ),
    );
  }

  /**
   * Whether the printed-order record shows an automatic job has nothing to
   * print: its receipt came out, or it is a recovery job for a printer the pass
   * did not find owed.
   */
  private alreadyHandled(job: AutoprintQueueElement): boolean {
    const records = this.printedOrderRecords();
    const key = printerKey(job.printer);
    if (job.autoprint.origin === 'recovery') {
      // Reconciliation prints only what a printer still owes: never a receipt
      // that came out, and never on a printer the pass did not find owed.
      if (printedRecordFor(records, job.orderId, key)) {
        return true;
      }
      return Array.isArray(job.autoprint.printers)
        ? !job.autoprint.printers.includes(key)
        : recoveryTargets(records, job.orderId, [key]).length === 0;
    }
    const printed = printedRecordFor(records, job.orderId, key);
    return (
      !!printed && isSameOrOlderVersion(job.autoprint.version, printed.version)
    );
  }

  /**
   * Whether jobs already in the queue for the same order and printer cover
   * `job`.
   *
   * A push announcing a newer version of the order is not covered by a job
   * that has started printing the old one — that receipt is out of date — but
   * one still waiting simply takes the newer version: the receipt is fetched
   * when the job prints, so it will show the order as it is then. A job with no
   * version to compare (a print staff asked for by hand) prints the order as it
   * is, so it covers any push.
   */
  private coveredByQueuedJobs(
    waiting: AutoprintQueueElement[],
    job: AutoprintQueueElement,
  ): boolean {
    if (
      job.autoprint.origin !== 'push' ||
      !Number.isFinite(job.autoprint.version)
    ) {
      return true;
    }
    const version = job.autoprint.version;
    if (
      waiting.some(
        (queued) =>
          !Number.isFinite(queued.autoprint?.version) ||
          isSameOrOlderVersion(version, queued.autoprint.version),
      )
    ) {
      return true;
    }
    const notStarted = waiting.find((queued) => queued.status !== 'INPROGRESS');
    if (notStarted) {
      notStarted.autoprint = { ...notStarted.autoprint, version };
      return true;
    }
    return false;
  }

  /**
   * Both copies of the printed-order record: the slice, and what survived the
   * last time Android killed the process (see `AUTOPRINT_PRINTED_ORDERS_KEY`).
   */
  private printedOrderRecords(): PrintedOrderRecord[] {
    let inMemory: PrintedOrderRecord[] = [];
    this.store
      .select(getRecentlyPrintedOrders)
      .pipe(take(1))
      .subscribe((records) => (inMemory = records ?? []));
    return [
      ...inMemory,
      ...toPrintedOrderRecords(
        this.storageService.getSavedState(AUTOPRINT_PRINTED_ORDERS_KEY),
      ),
    ];
  }

  clearAutoPrintQueue(storeId: number) {
    this.autoPrintQueue$.pipe(take(1)).subscribe((autoprintQueue) => {
      const updatedAutoprintQueue = autoprintQueue.filter(
        (elem) => elem.storeId !== storeId,
      );
      this.store.dispatch(new UpdateAutoprintQueue(updatedAutoprintQueue));
    });
    return of(true);
  }

  /**
   * Fetches one order's receipt image and sends it to `printer`.
   *
   * Emits whether the printer actually consumed the bytes. Every failure along
   * the way — an unreachable printer, an unknown transport, a receipt image
   * that will not load — is caught and reported here rather than thrown, so
   * that one bad job cannot wedge the queue; which makes this boolean the only
   * signal a caller has that the receipt came out. `drainAutoPrintQueue` needs
   * exactly that to decide whether the order may be marked printed or has to
   * stay eligible for foreground reconciliation. Card #4514.
   */
  autoPrint(
    printer: PrinterInfo,
    storeId: number,
    orderId: string,
  ): Observable<boolean> {
    return this.http
      .get(`/api/v1/stores/${storeId}/orders/${orderId}/invoice?toImage=true`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        take(1),
        switchMap(async (r) => {
          const blobResObj = this.toOrderCodeResponse(r as HttpResponse<Blob>);
          // Toasts name the order by the code the merchant sees in the order
          // list; the id stays in the log lines, where support needs it.
          const orderCode = this.orderCodeFrom(blobResObj.filename) || orderId;

          const img = new Image();
          const blobUrl = URL.createObjectURL(blobResObj.blob);

          const self = this;

          // Every exit below releases the image first. The error handler goes
          // before `src` is cleared: clearing `src` fires `error`, and with the
          // handler still attached every successful print also logged "Could
          // not load the invoice image".
          const release = () => {
            img.onload = null;
            img.onerror = null;
            img.src = '';
            URL.revokeObjectURL(blobUrl);
          };

          const loader = new Promise<boolean>((resolve) => {
            img.onload = function () {
              // ESC @ first, so state left by the previous order cannot leak
              // into this one; then the invoice as small image bands, so a job
              // that breaks off can never spoil the next receipt. See
              // esc-pos-job.ts.
              let job: Uint8Array;
              try {
                job = imageJob(img, printer.paperWidth);
              } catch (error) {
                // Anything thrown in here used to leave this promise pending
                // for good — and the queue with it, since the drain holds
                // printTaskCompleted until it settles.
                self.logger.error(
                  `Could not build the receipt for order ${orderId}: ` +
                    (error as Error).message,
                );
                self.notifyOrderNotPrinted(printer, orderCode);
                // Nothing reached the printer: false, so the order stays
                // eligible for foreground reconciliation (card #4514).
                resolve(false);
                return;
              } finally {
                release();
              }

              // One call for every transport. The plugin resolves the mode to
              // RFCOMM, GATT or a socket, and — the part that matters — only
              // reports once the printer has taken every byte, so a `true`
              // here means the printer consumed the bytes rather than merely
              // that a write call returned.
              //
              // It never rejects: a transport failure, an unknown mode and a
              // printer registered without an address all resolve false, having
              // been logged. That keeps this the only place the queue can
              // settle from, which is what stops one bad job stalling the
              // 5-second drain forever.
              //
              // The order id goes to the plugin service for its log lines, so
              // a failed or cut-off receipt in Kibana names the order.
              const printing = self.goPrinterService.print(
                printer,
                job,
                'auto',
                false,
                `order ${orderId}`,
              );
              printing.then(
                (printed) => {
                  if (printed) {
                    self.notify('success', 'admin.printer.orderPrinted', {
                      orderCode,
                    });
                  } else {
                    self.notifyOrderNotPrinted(printer, orderCode);
                  }
                  // The plugin's own verdict, not merely "the call returned".
                  // It resolves false for a transport failure, an unknown mode
                  // and a printer registered without an address, and the
                  // caller has to be able to tell those from a printed
                  // receipt.
                  resolve(printed);
                },
                (err) => {
                  // The details are for support, not for the merchant.
                  self.logger.error(
                    `Could not print order ${orderId}: ` + JSON.stringify(err),
                  );
                  self.notifyOrderNotPrinted(printer, orderCode);
                  resolve(false);
                },
              );
            };

            // Settle on failure too. This handler used to be assigned outside
            // the executor, so it had no access to resolve: an invoice image
            // that failed to load left the promise pending forever, and since
            // printTaskCompleted was already false, the 5-second drain never
            // started another job. One failed image stopped every subsequent
            // order printing until the app was restarted.
            img.onerror = function () {
              release();
              self.logger.error(
                'Could not load the invoice image for order ' + orderId,
              );
              // This order did not print either, so the merchant hears so.
              self.notifyOrderNotPrinted(printer, orderCode);
              resolve(false);
            };
          });
          img.src = blobUrl;

          // The plugin's verdict, not the invoice: `drainAutoPrintQueue` marks the
          // order printed off this value (card #4514).
          return await loader.catch((err) => {
            release();
            this.logger.error(err);
            return false;
          });
        }),
      );
  }

  testPrint(printer: PrinterInfo): Promise<boolean> {
    const printerMode = printer.mode;
    const printerName = printer.name;
    const connectionParamsIp = printer.ip;
    const connectionParamsPort = printer.port;
    const connectionParamsBluetooth = printer.macAddr;
    const connectionParamsPaperWidthInMM = printer.paperWidth;

    return new Promise((resolve) => {
      const img = new Image();
      const encoder = new EscPosEncoder();

      const isBluetooth =
        printerMode === 'Bluetooth' || printerMode === 'Bluetooth Classic';
      const testSuccessfulText = isBluetooth
        ? 'Bluetooth MAC : ' + connectionParamsBluetooth
        : 'IP : ' + connectionParamsIp + '  Port : ' + connectionParamsPort;
      // Two buffers are built here and transmitted in order: this text one
      // first, then the image one below. The cut therefore belongs on the
      // IMAGE buffer — putting it here would sever the paper between the
      // header and the test image and leave the image on a second, uncut slip.
      const resultByte: Uint8Array = encoder
        .initialize()
        .align('center')
        .line('Congratulation, ' + printerName + ' print success')
        .line(testSuccessfulText)
        .newline()
        .newline()
        .newline()
        .newline()
        .newline()
        .newline()
        .encode();

      // Settles with the image job, or with null if the test image could not
      // be loaded or drawn. It used to settle on success only, so a missing
      // test image left the test print hanging for good.
      const testImageLoader = new Promise<Uint8Array | null>(
        (resolveImageLoad) => {
          const release = () => {
            img.onload = null;
            img.onerror = null;
            img.src = '';
          };

          img.onload = () => {
            let job: Uint8Array | null = null;
            try {
              // The same builder as an order's receipt: ESC @, then the image
              // as small bands. See esc-pos-job.ts.
              job = imageJob(img, connectionParamsPaperWidthInMM);
            } catch (error) {
              this.logger.error(
                'Could not build the test image: ' + (error as Error).message,
              );
            }
            release();
            resolveImageLoad(job);
          };

          img.onerror = () => {
            release();
            resolveImageLoad(null);
          };
        },
      );
      img.src = 'assets/printer-test-image.png';

      testImageLoader
        .then((resultByteImage) => {
          if (!resultByteImage) {
            this.notify('error', 'admin.printer.testImageLoadFailed');
            resolve(false);
            // Nothing to print: without this return, the header still went
            // out, followed by a job made of `null`.
            return;
          }
          this.notify('info', 'admin.printer.testPrinting');

          // Two jobs, in order: the text buffer first, then the image buffer
          // that ends the receipt. Every transport takes the same path — the
          // plugin resolves the printer's mode to RFCOMM, GATT or a socket.
          //
          // Sending them as two separate jobs rather than one concatenated
          // buffer is deliberate and matches what the classic path already did:
          // each job is connected, drained and closed on its own, so a failure
          // names which of the two halves did not arrive. The plugin serialises
          // jobs, so the second queues behind the first instead of opening a
          // competing connection to the same printer — which is what the old
          // WiFi path's bare 500ms setTimeout was standing in for.
          //
          // The `'none'` on the first job is load-bearing. The plugin ends
          // every job it is given, so without it the header would be cut off
          // and the test image would come out on a second slip — which is
          // exactly the defect PR #6727 shipped by putting the cut on the text
          // buffer. The `true` on the second job is its pair: it tells the
          // plugin this job is the rest of the same receipt, so the plugin
          // does not cut the header off as a receipt that never ended.
          this.goPrinterService
            .print(printer, resultByte, 'none', false, 'test print')
            .then((printed) => {
              if (!printed) {
                this.notify('error', 'admin.printer.testTextFailed');
                resolve(false);
                return;
              }
              this.notify('info', 'admin.printer.testTextPrinted');

              this.goPrinterService
                .print(printer, resultByteImage, 'auto', true, 'test print')
                .then((imagePrinted) => {
                  if (imagePrinted) {
                    this.notify('success', 'admin.printer.testPrinted');
                  } else {
                    this.notify('error', 'admin.printer.testImageFailed');
                  }
                  resolve(imagePrinted);
                })
                .catch((err) => {
                  this.logger.error(
                    'Printer Image Connection Error: ' + JSON.stringify(err),
                  );
                  this.notify('error', 'admin.printer.testImageFailed');
                  resolve(false);
                });
            })
            .catch((err) => {
              this.logger.error(
                'Printer Connection Error : ' + JSON.stringify(err),
              );
              this.notify('error', 'admin.printer.testTextFailed');
              resolve(false);
            });
        })
        .catch((err) => {
          this.logger.error(
            'Printer Test Failed While Loading Test Image : ' +
              JSON.stringify(err),
          );
          resolve(false);
        });
    });
  }

  private toOrderCodeResponse(response: HttpResponse<Blob>): OrderPdfResponse {
    const blob = response.body;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      .slice(contentDisposition.indexOf('filename=') + 9)
      .replace(/"/g, '');
    return { blob, filename };
  }

  /**
   * The order code the merchant sees in the order list, read from the invoice
   * file name the server sends: `<store name>-order-<order code>.png`, URL
   * encoded. The last `-order-` is the one that counts, because a store name
   * may contain the same word. Empty when the name has some other shape.
   */
  private orderCodeFrom(filename: string): string {
    let name = filename ?? '';
    try {
      name = decodeURIComponent(name);
    } catch {
      // A name that is not valid URL encoding is read as it is.
    }
    const marker = '-order-';
    const at = name.lastIndexOf(marker);
    if (at < 0) {
      return '';
    }
    return name.slice(at + marker.length).replace(/\.[^.]*$/, '');
  }

  /**
   * One message for every way an order can fail to print, so the merchant
   * reads the same words whether the receipt could not be built, the image
   * could not be loaded or the printer did not take it.
   */
  private notifyOrderNotPrinted(printer: PrinterInfo, orderCode: string) {
    const modeKey = PRINTER_MODE_LABELS[printer.mode];
    this.notify('error', 'admin.printer.orderPrintFailed', {
      orderCode,
      mode: modeKey ? this.translate.instant(modeKey) : (printer.mode ?? ''),
    });
  }

  /**
   * Shows a translated printer message. The kind follows the outcome: only a
   * failure is an error toast, so a test print that passed no longer looks
   * like one that failed.
   */
  private notify(
    kind: 'success' | 'info' | 'error',
    key: string,
    params?: Record<string, string>,
  ) {
    this.toastr[kind](null, this.translate.instant(key, params), {
      toastComponent: GoToast,
    });
  }

  ngOnDestroy() {
    // Clear interval to prevent memory leaks
    if (this.autoprintQueueInterval !== null) {
      clearInterval(this.autoprintQueueInterval);
      this.autoprintQueueInterval = null;
    }

    // Complete subject
    this.autoprintQueueDestroy$.next();
    this.autoprintQueueDestroy$.complete();
  }
}

/** One receipt on one printer: the unit the autoprint queue works in. */
function jobKey(job: AutoprintQueueElement): string {
  return `${job.orderId}\u0000${printerKey(job.printer)}`;
}
